import { useState, useEffect } from 'react';
import { 
    Plus, 
    Search, 
    Upload,
    FileText,
    File,
    Trash2,
    Download,
    Eye,
    Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
    { value: 'bol', label: 'Bill of Lading (BOL)' },
    { value: 'pod', label: 'Proof of Delivery (POD)' },
    { value: 'invoice', label: 'Invoice' },
    { value: 'ratecon', label: 'Rate Confirmation' },
    { value: 'other', label: 'Other' }
];

export default function DocumentsPage() {
    const [documents, setDocuments] = useState([]);
    const [loads, setLoads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [showUploadDialog, setShowUploadDialog] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, document: null });
    const [deleting, setDeleting] = useState(false);

    const [uploadForm, setUploadForm] = useState({
        load_id: '',
        doc_type: 'other',
        filename: '',
        notes: ''
    });

    useEffect(() => {
        fetchData();
    }, [typeFilter]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (typeFilter && typeFilter !== 'all') params.append('document_type', typeFilter);

            const [docsRes, loadsRes] = await Promise.all([
                fetch(`${API_URL}/api/load-documents?${params}`, { credentials: 'include' }),
                fetch(`${API_URL}/api/loads`, { credentials: 'include' })
            ]);

            if (docsRes.ok) {
                const docsData = await docsRes.json();
                setDocuments(docsData);
            }
            if (loadsRes.ok) {
                const loadsData = await loadsRes.json();
                setLoads(loadsData);
            }
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Failed to fetch documents');
        } finally {
            setLoading(false);
        }
    };

    const handleUploadDocument = async () => {
        if (!uploadForm.filename) {
            toast.error('Please enter a filename');
            return;
        }

        setUploading(true);
        try {
            const params = new URLSearchParams();
            if (uploadForm.load_id) params.append('load_id', uploadForm.load_id);
            params.append('doc_type', uploadForm.doc_type);
            params.append('filename', uploadForm.filename);
            if (uploadForm.notes) params.append('notes', uploadForm.notes);

            const response = await fetch(`${API_URL}/api/documents?${params}`, {
                method: 'POST',
                credentials: 'include'
            });

            if (response.ok) {
                toast.success('Document record created successfully!');
                setShowUploadDialog(false);
                setUploadForm({
                    load_id: '',
                    doc_type: 'other',
                    filename: '',
                    notes: ''
                });
                fetchData();
            } else {
                toast.error('Failed to create document record');
            }
        } catch (error) {
            console.error('Failed to upload document:', error);
            toast.error('Failed to upload document');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteDocument = async (documentId) => {
        setDeleting(true);
        try {
            const response = await fetch(`${API_URL}/api/load-documents/${documentId}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (response.ok) {
                toast.success('Document deleted successfully');
                fetchData();
            } else {
                toast.error('Failed to delete document');
            }
        } catch (error) {
            console.error('Failed to delete document:', error);
            toast.error('Failed to delete document');
        } finally {
            setDeleting(false);
            setDeleteConfirmDialog({ open: false, document: null });
        }
    };

    const getDocTypeBadge = (type) => {
        const styles = {
            bol: 'doc-bol',
            pod: 'doc-pod',
            invoice: 'doc-invoice',
            rate_confirmation: 'doc-rate',
            ratecon: 'doc-rate',
            other: 'bg-zinc-700 text-zinc-300'
        };
        return styles[type] || styles.other;
    };

    const getStatusBadge = (status) => {
        const styles = {
            pending: 'status-pending',
            approved: 'status-active',
            rejected: 'status-delayed'
        };
        return styles[status] || 'status-pending';
    };

    const filteredDocuments = documents.filter(doc => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            doc.document_id?.toLowerCase().includes(query) ||
            doc.filename?.toLowerCase().includes(query) ||
            doc.load_id?.toLowerCase().includes(query)
        );
    });

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="documents-page">
            {/* Page Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white tracking-tight uppercase">Documents</h1>
                    <p className="text-zinc-500 text-sm mt-1">Manage BOLs, PODs, invoices, and other documents</p>
                </div>
                <Button 
                    onClick={() => setShowUploadDialog(true)}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="upload-doc-btn"
                >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Document
                </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {DOC_TYPES.map((type) => {
                    const count = documents.filter(d => d.document_type === type.value || d.doc_type === type.value).length;
                    return (
                        <Card 
                            key={type.value}
                            className={`bg-zinc-900/50 border-zinc-800 cursor-pointer hover:border-zinc-700 transition-colors ${typeFilter === type.value ? 'border-orange-500' : ''}`}
                            onClick={() => setTypeFilter(typeFilter === type.value ? 'all' : type.value)}
                        >
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-zinc-400 text-sm">{type.label.split(' (')[0]}</span>
                                    <Badge className={`${getDocTypeBadge(type.value)} text-xs`}>
                                        {count}
                                    </Badge>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* Filters */}
            <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                            <Input
                                placeholder="Search by ID, filename, or load..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 bg-zinc-900 border-zinc-800"
                                data-testid="search-input"
                            />
                        </div>
                        <Select
                            value={typeFilter}
                            onValueChange={setTypeFilter}
                        >
                            <SelectTrigger className="w-full md:w-48 bg-zinc-900 border-zinc-800" data-testid="type-filter">
                                <SelectValue placeholder="All Types" />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                                <SelectItem value="all">All Types</SelectItem>
                                {DOC_TYPES.map(type => (
                                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Documents Table */}
            <Card className="bg-zinc-900/50 border-zinc-800" data-testid="documents-table-card">
                <CardHeader className="border-b border-zinc-800 pb-4">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide">
                            All Documents
                        </CardTitle>
                        <Badge variant="outline" className="border-zinc-700 text-zinc-400">
                            {filteredDocuments.length} documents
                        </Badge>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : filteredDocuments.length === 0 ? (
                        <div className="empty-state py-12">
                            <FileText className="w-12 h-12 text-zinc-700 mb-4" />
                            <p className="text-zinc-500">No documents found</p>
                            <Button 
                                variant="outline" 
                                className="mt-4 border-zinc-700"
                                onClick={() => setShowUploadDialog(true)}
                            >
                                Upload Your First Document
                            </Button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Document ID</th>
                                        <th>Type</th>
                                        <th>Filename</th>
                                        <th>Load ID</th>
                                        <th>Status</th>
                                        <th>Created</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredDocuments.map((doc) => (
                                        <tr key={doc.document_id} data-testid={`doc-row-${doc.document_id}`}>
                                            <td className="text-orange-400">{doc.document_id}</td>
                                            <td>
                                                <Badge className={`${getDocTypeBadge(doc.document_type || doc.doc_type)} text-xs uppercase`}>
                                                    {(doc.document_type || doc.doc_type)?.replace('_', ' ')}
                                                </Badge>
                                            </td>
                                            <td className="text-white">
                                                <div className="flex items-center gap-2">
                                                    <File className="w-4 h-4 text-zinc-500" />
                                                    {doc.filename || doc.original_filename}
                                                </div>
                                            </td>
                                            <td className="text-zinc-400">{doc.load_id || '-'}</td>
                                            <td>
                                                <Badge className={`${getStatusBadge(doc.status)} text-xs uppercase`}>
                                                    {doc.status}
                                                </Badge>
                                            </td>
                                            <td className="text-zinc-400">{formatDate(doc.created_at)}</td>
                                            <td>
                                                <div className="flex items-center gap-1">
                                                    {doc.file_path && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-zinc-400 hover:text-white hover:bg-zinc-800"
                                                            title="View"
                                                            data-testid={`view-doc-${doc.document_id}`}
                                                            onClick={async () => {
                                                                // Use signed_file_path if available, otherwise file_path
                                                                let filePath = doc.signed_file_path || doc.file_path;
                                                                // Strip "uploads/" prefix if present (normalize path)
                                                                if (filePath.startsWith('uploads/')) {
                                                                    filePath = filePath.substring(8);
                                                                }
                                                                const url = `${API_URL}/api/uploads/${filePath}`;
                                                                try {
                                                                    // First check if file exists
                                                                    const response = await fetch(url, { 
                                                                        method: 'HEAD',
                                                                        credentials: 'include'
                                                                    });
                                                                    if (response.ok) {
                                                                        window.open(url, '_blank');
                                                                    } else {
                                                                        toast.error('File not found on server');
                                                                    }
                                                                } catch (error) {
                                                                    // If HEAD fails, try opening directly
                                                                    window.open(url, '_blank');
                                                                }
                                                            }}
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon" 
                                                        className="h-8 w-8 text-red-400 hover:text-red-300"
                                                        onClick={() => setDeleteConfirmDialog({ open: true, document: doc })}
                                                        title="Delete"
                                                        data-testid={`delete-doc-${doc.document_id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Upload Document Dialog */}
            <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-white">Upload Document</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            Create a new document record
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="text-zinc-400">Document Type *</Label>
                            <Select
                                value={uploadForm.doc_type}
                                onValueChange={(value) => setUploadForm({ ...uploadForm, doc_type: value })}
                            >
                                <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1" data-testid="doc-type-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                    {DOC_TYPES.map(type => (
                                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-zinc-400">Filename *</Label>
                            <Input
                                value={uploadForm.filename}
                                onChange={(e) => setUploadForm({ ...uploadForm, filename: e.target.value })}
                                className="bg-zinc-800 border-zinc-700 mt-1"
                                placeholder="BOL_LoadID_Date.pdf"
                                data-testid="filename-input"
                            />
                        </div>
                        {loads.length > 0 && (
                            <div>
                                <Label className="text-zinc-400">Associated Load</Label>
                                <Select
                                    value={uploadForm.load_id}
                                    onValueChange={(value) => setUploadForm({ ...uploadForm, load_id: value })}
                                >
                                    <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1" data-testid="load-select">
                                        <SelectValue placeholder="Select load (optional)" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                                        {loads.map(load => (
                                            <SelectItem key={load.load_id} value={load.load_id}>
                                                {load.load_id} - {load.origin_city} → {load.destination_city}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div>
                            <Label className="text-zinc-400">Notes</Label>
                            <Input
                                value={uploadForm.notes}
                                onChange={(e) => setUploadForm({ ...uploadForm, notes: e.target.value })}
                                className="bg-zinc-800 border-zinc-700 mt-1"
                                placeholder="Additional notes..."
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button 
                            variant="outline" 
                            onClick={() => setShowUploadDialog(false)}
                            className="border-zinc-700"
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleUploadDocument}
                            disabled={uploading}
                            className="bg-orange-500 hover:bg-orange-600 text-white"
                            data-testid="submit-doc-btn"
                        >
                            {uploading ? 'Creating...' : 'Create Record'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirmDialog.open} onOpenChange={(open) => !open && setDeleteConfirmDialog({ open: false, document: null })}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-white">Delete Document</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            Are you sure you want to delete this document? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {deleteConfirmDialog.document && (
                        <div className="py-4 px-4 bg-zinc-800/50 rounded-md">
                            <p className="text-white font-medium">{deleteConfirmDialog.document.filename || deleteConfirmDialog.document.original_filename}</p>
                            <p className="text-zinc-400 text-sm mt-1">
                                Type: {(deleteConfirmDialog.document.document_type || deleteConfirmDialog.document.doc_type)?.toUpperCase()}
                            </p>
                            <p className="text-zinc-500 text-xs mt-1">
                                ID: {deleteConfirmDialog.document.document_id}
                            </p>
                        </div>
                    )}

                    <DialogFooter>
                        <Button 
                            variant="outline" 
                            onClick={() => setDeleteConfirmDialog({ open: false, document: null })}
                            className="border-zinc-700"
                            disabled={deleting}
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={() => handleDeleteDocument(deleteConfirmDialog.document?.document_id)}
                            disabled={deleting}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid="confirm-delete-btn"
                        >
                            {deleting ? 'Deleting...' : 'Delete Document'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
